import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { Calendar, PlayCircle, BadgeCheck, ShieldCheck, Award, MapPin, PhoneCall } from "lucide-react";
import heroImg from "@/assets/neil-pudu-bbb-hero.png.asset.json";

const HeroSection = () => {
  return (
    <section
      aria-labelledby="hero-heading"
      className="animate-fade-in w-full"
    >
      {/* Hero image — full-width, contain, with subtle blue glow */}
      <div className="mx-auto w-full max-w-[1280px]">
        <div
          className="relative overflow-hidden rounded-[20px] md:rounded-[28px] border border-primary/25 bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.18),transparent_55%)]"
          style={{
            boxShadow:
              "0 0 40px hsl(var(--primary) / 0.22), 0 10px 40px rgba(0,0,0,0.5)",
          }}
        >
          <div className="pointer-events-none absolute -top-24 -left-24 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-56 w-56 rounded-full bg-[hsl(43_75%_55%)]/20 blur-3xl" />
          <img
            src={heroImg.url}
            alt="Neil AI Global Solutions and PUDU Robotics commercial robots with BBB Accredited Business logo."
            width={1536}
            height={1024}
            loading="eager"
            {...({ fetchpriority: "high" } as any)}
            decoding="async"
            className="relative block w-full h-auto object-contain"
          />
        </div>
      </div>

      {/* Crawlable hero copy — sits 24px+ below the image */}
      <div className="mx-auto w-full max-w-[1100px] mt-6 md:mt-10 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-4">
          <BadgeCheck className="h-3.5 w-3.5" />
          AI Automation & Commercial Robotics
        </span>

        <h1
          id="hero-heading"
          className="font-orbitron font-bold leading-[1.05] text-[clamp(30px,5.5vw,64px)] mb-5"
        >
          AI Automation & Robotics for{" "}
          <span className="bg-gradient-to-r from-foreground via-foreground to-[hsl(43_85%_62%)] bg-clip-text text-transparent">
            Modern Businesses
          </span>
        </h1>

        <p className="text-[clamp(15px,1.7vw,19px)] mb-7 mx-auto max-w-[820px] leading-relaxed font-medium text-muted-foreground">
          Neil AI Global Solutions helps businesses reduce repetitive work, improve customer
          experience, and increase operational efficiency with AI employees, AI voice agents,
          CRM automation, and commercial service robots — backed by local deployment and human support.
        </p>

        <div className="flex gap-3 flex-wrap justify-center mb-6">
          <Button
            asChild
            size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] font-extrabold rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg min-h-[52px] transition-all duration-300 shadow-[0_0_30px_hsl(var(--primary)/0.45)] hover:shadow-[0_0_45px_hsl(var(--primary)/0.7)]"
          >
            <a
              href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Calendar className="h-5 w-5" />
              Book a Free Demo
            </a>
          </Button>

          <Button
            asChild
            variant="outline"
            size="lg"
            className="border-2 border-primary/40 hover:bg-primary/10 hover:border-primary/70 font-bold rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg min-h-[52px] transition-all duration-300"
          >
            <Link to="/videos" className="inline-flex items-center gap-2">
              <PlayCircle className="h-5 w-5" />
              Watch Robots in Action
            </Link>
          </Button>

          <Button
            asChild
            variant="ghost"
            size="lg"
            className="font-bold rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg min-h-[52px] text-foreground hover:bg-white/5"
          >
            <a href="tel:+13369624539" className="inline-flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-primary" />
              Call (336) 962-4539
            </a>
          </Button>
        </div>

        {/* Trust-based statements (no unsupported stats) */}
        <ul className="flex flex-wrap items-center justify-center gap-2.5 mb-2">
          <li className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background/60 px-3 py-1.5 text-xs sm:text-sm font-semibold">
            <MapPin className="h-3.5 w-3.5 text-primary" /> North Carolina Based
          </li>
          <li className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--line))] bg-white/[0.04] px-3 py-1.5 text-xs sm:text-sm font-semibold">
            <Award className="h-3.5 w-3.5 text-[hsl(43_75%_55%)]" /> PUDU Robotics Authorized Dealer
          </li>
          <li className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--line))] bg-white/[0.04] px-3 py-1.5 text-xs sm:text-sm font-semibold">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> BBB Accredited Business
          </li>
          <li className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--line))] bg-white/[0.04] px-3 py-1.5 text-xs sm:text-sm font-semibold">
            <BadgeCheck className="h-3.5 w-3.5 text-primary" /> Local Deployment Support
          </li>
          <li className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--line))] bg-white/[0.04] px-3 py-1.5 text-xs sm:text-sm font-semibold">
            <Award className="h-3.5 w-3.5 text-[hsl(43_75%_55%)]" /> AI Automation Experts
          </li>
        </ul>
      </div>

      {/* SEO-rich crawlable content block */}
      <div className="mx-auto w-full max-w-[1100px] mt-10 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          {
            h: "AI Automation Solutions",
            p: "AI employees, workflow automation, and intelligent business systems that eliminate repetitive tasks and scale operations.",
          },
          {
            h: "Commercial Robotics Solutions",
            p: "Service, delivery, and cleaning robots deployed for restaurants, hotels, warehouses, schools, churches, and medical facilities.",
          },
          {
            h: "PUDU Robotics Authorized Dealer",
            p: "Official dealer of BellaBot Pro, KettyBot Pro, CC1 Pro, MT1, T300, T600, FlashBot Max, and the full PUDU robotics lineup.",
          },
          {
            h: "AI Voice Agents",
            p: "24/7 AI receptionists and voice agents that answer calls, qualify leads, and book appointments directly into your CRM.",
          },
          {
            h: "Business Automation",
            p: "CRM automation, AI appointment booking, marketing automation, and reporting dashboards that reduce overhead.",
          },
          {
            h: "Commercial Cleaning Robots",
            p: "Autonomous floor scrubbers and cleaning robots that cut labor costs and deliver consistent results overnight.",
          },
        ].map((item) => (
          <div
            key={item.h}
            className="rounded-2xl border border-[hsl(var(--line))] bg-white/[0.03] p-5 backdrop-blur-md"
          >
            <h2 className="text-base md:text-lg font-bold text-foreground mb-1.5">
              {item.h}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{item.p}</p>
          </div>
        ))}
      </div>

      {/* Local SEO crawlable paragraph */}
      <div className="mx-auto w-full max-w-[1100px] mt-8 md:mt-10 rounded-2xl border border-[hsl(var(--line))] bg-background/40 p-5 md:p-6 backdrop-blur-md">
        <h2 className="text-base md:text-lg font-bold text-foreground mb-2">
          Service Robots & AI Automation Across North Carolina
        </h2>
        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
          Neil AI Global Solutions serves businesses across North Carolina and helps restaurants,
          hotels, warehouses, schools, churches, medical offices, senior living facilities, retail
          stores, and commercial properties adopt AI automation and robotics with local deployment,
          training, and human support.
        </p>
      </div>
    </section>
  );
};

export default HeroSection;
